// Customer Display Page
// 7-10인치 소형 화면용 - 단순하고 큰 글씨로 핵심 정보만 표시

import { useState, useCallback, useEffect, useRef } from 'react'
import {
  useCustomerDisplayChannel,
  TransactionUpdatePayload,
  ExchangeRateUpdatePayload,
  DisplayModeChangePayload,
} from '../hooks/useBroadcastChannel'

type DisplayMode = 'idle' | 'transaction'

interface RateInfo {
  currency: string
  buy: number
  sell: number
}

// Currency → country code for flag-icons
const CURRENCY_COUNTRY: Record<string, string> = {
  USD: 'us', EUR: 'eu', JPY: 'jp', CNY: 'cn', GBP: 'gb',
  AUD: 'au', CAD: 'ca', CHF: 'ch', TWD: 'tw', HKD: 'hk',
  SGD: 'sg', THB: 'th', VND: 'vn', MYR: 'my', PHP: 'ph',
  IDR: 'id',
}

// Currency → language for parenthetical labels
const CURRENCY_LANG: Record<string, 'ja' | 'zh' | 'en'> = {
  JPY: 'ja', CNY: 'zh', TWD: 'zh',
}

const LANG_LABELS = {
  rate:          { en: 'Rate',           ja: 'レート',       zh: '汇率' },
  totalWon:      { en: 'Total Won',      ja: '受取金額',     zh: '应收金额' },
  payment:       { en: 'Total Won',      ja: 'お支払金額',   zh: '支付金额' },
  foreignAmount: { en: 'Foreign Amount', ja: '受取外貨',     zh: '应收外币' },
  passport:      { en: 'Please present your passport or ID', ja: 'パスポートまたは身分証明書をご提示ください', zh: '请出示您的护照或身份证' },
}

function getLangLabel(currency: string, key: keyof typeof LANG_LABELS): string {
  const lang = CURRENCY_LANG[currency] || 'en'
  const en = LANG_LABELS[key].en
  // 통화별 언어와 영어 병기 (#6-1: 제3국 손님이 읽을 수 있도록)
  if (lang === 'en') return en
  return `${LANG_LABELS[key][lang]} / ${en}`
}

function CustomerDisplay() {
  const [mode, setMode] = useState<DisplayMode>('idle')
  const [transaction, setTransaction] = useState<TransactionUpdatePayload | null>(null)
  const [exchangeRates, setExchangeRates] = useState<RateInfo[]>([])
  const [branchName, setBranchName] = useState<string>('')
  const [currentTime, setCurrentTime] = useState(new Date())
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 시계 업데이트
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(interval)
  }, [])

  // 1분 비활성 타이머 시작 (rate-preview/processing 상태에서 호출)
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => {
      setMode('idle')
      setTransaction(null)
    }, 60_000)
  }, [])

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
  }, [])

  const handleTransactionUpdate = useCallback((payload: TransactionUpdatePayload) => {
    if (payload.status === 'idle') {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      setMode('idle')
      setTransaction(null)
    } else if (payload.status === 'complete') {
      // 거래 완료 화면은 '새 거래' 전까지 계속 유지 — 비활성 타이머 걸지 않음 (#6-2)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      setTransaction(payload)
      setMode('transaction')
    } else {
      setTransaction(payload)
      setMode('transaction')
      resetIdleTimer()
    }
  }, [resetIdleTimer])

  const handleExchangeRateUpdate = useCallback((payload: ExchangeRateUpdatePayload) => {
    if (payload.branchName) {
      setBranchName(payload.branchName)
    }
    // 환율 정보를 배열로 변환
    const ratesArray: RateInfo[] = Object.entries(payload.rates).map(([currency, rate]) => ({
      currency,
      buy: rate.buy,
      sell: rate.sell,
    }))
    // 주요 통화 순서로 정렬
    const order = ['USD', 'JPY', 'EUR', 'CNY', 'GBP', 'AUD', 'CAD', 'CHF']
    ratesArray.sort((a, b) => {
      const aIdx = order.indexOf(a.currency)
      const bIdx = order.indexOf(b.currency)
      if (aIdx === -1 && bIdx === -1) return a.currency.localeCompare(b.currency)
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
    setExchangeRates(ratesArray)
  }, [])

  const handleDisplayModeChange = useCallback((payload: DisplayModeChangePayload) => {
    if (payload.mode === 'idle') {
      setMode('idle')
      setTransaction(null)
      return
    }
    // 'complete' 모드에서는 현재 거래 화면 유지 (새 거래 버튼 누르기 전까지)
    // transaction 모드 전환은 handleTransactionUpdate에서 처리
  }, [])

  useCustomerDisplayChannel(
    handleTransactionUpdate,
    handleExchangeRateUpdate,
    handleDisplayModeChange,
  )

  return (
    <div className="customer-display">
      <style>{styles}</style>

      {mode === 'idle' || !transaction ? (
        <IdleView rates={exchangeRates} time={currentTime} branchName={branchName} />
      ) : transaction.status === 'rate-preview' ? (
        <RatePreviewView transaction={transaction} rates={exchangeRates} />
      ) : (
        <TransactionView transaction={transaction} />
      )}
    </div>
  )
}

// 대기 화면 - 환율 순차 슬라이드
function IdleView({ rates, time, branchName }: { rates: RateInfo[]; time: Date; branchName: string }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  // 3초마다 다음 환율로 전환
  useEffect(() => {
    if (rates.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % rates.length)
    }, 3000)

    return () => clearInterval(interval)
  }, [rates.length])

  const currentRate = rates[currentIndex]
  const countryCode = currentRate ? CURRENCY_COUNTRY[currentRate.currency] : null

  return (
    <div className="idle-view">
      <header className="idle-header">
        <img src="/images/icon/logo_white.png" alt="ABC Money Exchange" className="logo" />
        <div className="time">
          {time.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </header>

      <div className="rate-display">
        {rates.length === 0 ? (
          <div className="waiting">
            <div className="waiting-icon">💱</div>
            <div className="waiting-text">환율 정보 대기중</div>
            <div className="waiting-sub">Staff 화면에서 로그인해주세요</div>
          </div>
        ) : currentRate ? (
          <div className="rate-card" key={currentRate.currency}>
            <div className="currency-header">
              {countryCode && (
                <span className={`fi fi-${countryCode} currency-flag`} />
              )}
              <span className="currency-name">{currentRate.currency}</span>
            </div>
            <div className="rate-row">
              <div className="rate-item buy">
                <span className="rate-label">Sell</span>
                <span className="rate-value">{currentRate.buy.toLocaleString()}</span>
              </div>
              <div className="rate-item sell">
                <span className="rate-label">Buy</span>
                <span className="rate-value">{currentRate.sell.toLocaleString()}</span>
              </div>
            </div>
            <div className="rate-indicator">
              {rates.map((_, i) => (
                <span key={i} className={`dot ${i === currentIndex ? 'active' : ''}`} />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <footer className="idle-footer">
        {branchName || 'ABC Money Exchange'}
      </footer>
    </div>
  )
}

// 환율 미리보기 화면 - 통화 선택 시 해당 통화의 환율 정보 표시
function RatePreviewView({ transaction, rates }: { transaction: TransactionUpdatePayload; rates: RateInfo[] }) {
  const { currency, tradeType } = transaction
  const countryCode = CURRENCY_COUNTRY[currency]
  const rateInfo = rates.find(r => r.currency === currency)
  const isBuy = tradeType === 'buy'
  const tickerRates = rates.filter(r => r.currency !== currency)

  const tickerText = tickerRates.map(r => {
    const cc = CURRENCY_COUNTRY[r.currency]
    const rate = isBuy ? r.buy : r.sell
    return `${cc ? `<span class="fi fi-${cc}" style="font-size:28px;border-radius:4px;margin-right:8px;vertical-align:middle"></span>` : ''}${r.currency} ${rate.toLocaleString()}`
  }).join(' &nbsp;&nbsp;|&nbsp;&nbsp; ')

  return (
    <div className="rate-preview-view">
      <div className="rate-preview-header">
        {countryCode && (
          <span className={`fi fi-${countryCode} rate-preview-flag`} />
        )}
        <span className="rate-preview-currency">{currency}</span>
      </div>

      <div className="rate-preview-content">
        <div className="rate-preview-single">
          <span className="rate-preview-single-value">
            {(isBuy ? (rateInfo?.buy ?? transaction.rate) : (rateInfo?.sell ?? transaction.rate)).toLocaleString()}
            <small> 원</small>
          </span>
        </div>
      </div>

      <div className="rate-preview-footer">
        {isBuy ? 'Selling' : 'Buying'} {currency}
      </div>

      {tickerRates.length > 0 && (
        <div className="rate-ticker-bar">
          <div
            className="rate-ticker-content"
            dangerouslySetInnerHTML={{ __html: tickerText + ' &nbsp;&nbsp;|&nbsp;&nbsp; ' + tickerText }}
          />
        </div>
      )}
    </div>
  )
}

// 거래 화면 - 핵심 정보만 크게
function TransactionView({ transaction }: { transaction: TransactionUpdatePayload | null }) {
  if (!transaction) return null

  const { tradeType, currency, amount, rate, krwAmount, lines, totalKRW, exchange } = transaction
  const isBuy = tradeType === 'buy'
  const countryCode = CURRENCY_COUNTRY[currency]

  // 통화간 교환 거래 (#7): 내는 통화 → 받는 통화 + 원화 거스름돈
  if (exchange) {
    const giveCC = CURRENCY_COUNTRY[exchange.give.currency]
    const receiveCC = CURRENCY_COUNTRY[exchange.receive.currency]
    return (
      <div className="transaction-view buy">
        <div className="trade-type-badge">Exchange</div>

        <div className="multi-lines">
          <div className="multi-line">
            <span className="multi-line-currency">
              {giveCC && <span className={`fi fi-${giveCC} multi-line-flag`} />}
              {exchange.give.currency}
            </span>
            <span className="multi-line-amount">
              {exchange.give.amount.toLocaleString()} <small>{exchange.give.currency}</small>
            </span>
            <span className="multi-line-rate">@ {exchange.give.rate.toLocaleString()}</span>
            <span className="multi-line-krw">{exchange.give.krwAmount.toLocaleString()} 원</span>
          </div>
          <div className="multi-line">
            <span className="multi-line-currency">
              {receiveCC && <span className={`fi fi-${receiveCC} multi-line-flag`} />}
              {exchange.receive.currency}
            </span>
            <span className="multi-line-amount">
              {exchange.receive.amount.toLocaleString()} <small>{exchange.receive.currency}</small>
            </span>
            <span className="multi-line-rate">@ {exchange.receive.rate.toLocaleString()}</span>
            <span className="multi-line-krw">{exchange.receive.krwAmount.toLocaleString()} 원</span>
          </div>
        </div>

        <div className="info-grid">
          <div className="info-row highlight">
            <span className="label">받으실 외화 (You Receive)</span>
            <span className="value foreign">
              {exchange.receive.amount.toLocaleString()} <small>{exchange.receive.currency}</small>
            </span>
          </div>
          <div className="info-row highlight">
            <span className="label">거스름돈 (Change)</span>
            <span className="value krw">
              {exchange.changeKRW.toLocaleString()} <small>원</small>
            </span>
          </div>
        </div>

        <div className="status-bar">
          {transaction.status === 'complete' ? '거래 완료' : (
            <div className="passport-request">
              <div>Please present your passport or ID</div>
              <div>여권 또는 신분증을 제시해 주세요</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // 복수 거래 항목: 통화/환율별 리스트 + 원화 총액 (#5, #9)
  if (lines && lines.length > 1) {
    return (
      <div className={`transaction-view ${tradeType}`}>
        <div className="trade-type-badge">
          {isBuy ? 'Selling' : 'Buying'}
        </div>

        <div className="multi-lines">
          {lines.map((line, index) => {
            const cc = CURRENCY_COUNTRY[line.currency]
            return (
              <div className="multi-line" key={`${line.currency}:${line.rate}:${index}`}>
                <span className="multi-line-currency">
                  {cc && <span className={`fi fi-${cc} multi-line-flag`} />}
                  {line.currency}
                </span>
                <span className="multi-line-amount">
                  {line.amount.toLocaleString()} <small>{line.currency}</small>
                </span>
                <span className="multi-line-rate">@ {line.rate.toLocaleString()}</span>
                <span className="multi-line-krw">{line.krwAmount.toLocaleString()} 원</span>
              </div>
            )
          })}
        </div>

        <div className="info-grid">
          <div className="info-row highlight">
            <span className="label">{isBuy ? '받으실 금액' : '결제 금액'} (Total)</span>
            <span className="value krw">
              {(totalKRW ?? krwAmount).toLocaleString()} <small>원</small>
            </span>
          </div>
        </div>

        <div className="status-bar">
          {transaction.status === 'complete' ? '거래 완료' : (
            <div className="passport-request">
              <div>Please present your passport or ID</div>
              <div>여권 또는 신분증을 제시해 주세요</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`transaction-view ${tradeType}`}>
      <div className="trade-type-badge">
        {countryCode && (
          <span className={`fi fi-${countryCode} tx-flag`} />
        )}
        {isBuy ? 'Selling' : 'Buying'}
      </div>

      <div className="info-grid">
        {isBuy ? (
          <>
            <div className="info-row">
              <span className="label">외화 금액 ({getLangLabel(currency, 'foreignAmount')})</span>
              <span className="value foreign">
                {amount.toLocaleString()} <small>{currency}</small>
              </span>
            </div>

            <div className="info-row rate">
              <span className="label">적용 환율 ({getLangLabel(currency, 'rate')})</span>
              <span className="value">{rate.toLocaleString()} 원</span>
            </div>

            <div className="divider" />

            <div className="info-row highlight">
              <span className="label">받으실 금액 ({getLangLabel(currency, 'totalWon')})</span>
              <span className="value krw">
                {krwAmount.toLocaleString()} <small>원</small>
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="info-row">
              <span className="label">결제 금액 ({getLangLabel(currency, 'payment')})</span>
              <span className="value krw">
                {krwAmount.toLocaleString()} <small>원</small>
              </span>
            </div>

            <div className="info-row rate">
              <span className="label">적용 환율 ({getLangLabel(currency, 'rate')})</span>
              <span className="value">{rate.toLocaleString()} 원</span>
            </div>

            <div className="divider" />

            <div className="info-row highlight">
              <span className="label">받으실 외화 ({getLangLabel(currency, 'foreignAmount')})</span>
              <span className="value foreign">
                {amount.toLocaleString()} <small>{currency}</small>
              </span>
            </div>
          </>
        )}
      </div>

      <div className="status-bar">
        {transaction.status === 'complete' ? '거래 완료' : (
          <div className="passport-request">
            <div>Please present your passport or ID</div>
            <div>여권 또는 신분증을 제시해 주세요</div>
            {CURRENCY_LANG[currency] && CURRENCY_LANG[currency] !== 'en' && (
              <div>{LANG_LABELS.passport[CURRENCY_LANG[currency]]}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const styles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .customer-display {
    width: 100vw;
    height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    overflow: hidden;
  }

  /* ========== 대기 화면 ========== */
  .idle-view {
    height: 100%;
    background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%);
    color: white;
    display: flex;
    flex-direction: column;
    padding: 72px;
  }

  .idle-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 60px;
  }

  .logo {
    height: 72px;
    object-fit: contain;
  }

  .time {
    font-size: 42px;
    color: #94a3b8;
    font-weight: 500;
  }

  .rate-display {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .waiting {
    text-align: center;
  }

  .waiting-icon {
    font-size: 192px;
    margin-bottom: 48px;
  }

  .waiting-text {
    font-size: 84px;
    color: #94a3b8;
    margin-bottom: 24px;
  }

  .waiting-sub {
    font-size: 54px;
    color: #64748b;
  }

  .rate-card {
    text-align: center;
    animation: fadeIn 0.5s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .currency-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 48px;
    margin-bottom: 72px;
  }

  .currency-flag {
    font-size: 120px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  .currency-name {
    font-size: 168px;
    font-weight: 700;
    color: #fbbf24;
  }

  .rate-row {
    display: flex;
    gap: 144px;
    justify-content: center;
    margin-bottom: 96px;
  }

  .rate-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .rate-label {
    font-size: 60px;
    color: #94a3b8;
  }

  .rate-value {
    font-size: 126px;
    font-weight: 600;
  }

  .rate-item.buy .rate-value {
    color: #4ade80;
  }

  .rate-item.sell .rate-value {
    color: #60a5fa;
  }

  .rate-indicator {
    display: flex;
    gap: 24px;
    justify-content: center;
  }

  .dot {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(255,255,255,0.3);
    transition: all 0.3s;
  }

  .dot.active {
    background: #fbbf24;
    width: 72px;
    border-radius: 15px;
  }

  .idle-footer {
    text-align: center;
    color: #64748b;
    font-size: 48px;
  }

  /* ========== 거래 화면 ========== */
  .transaction-view {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 72px;
  }

  .transaction-view.buy {
    background: linear-gradient(135deg, #064e3b 0%, #022c22 100%);
    color: white;
  }

  .transaction-view.sell {
    background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%);
    color: white;
  }

  .trade-type-badge {
    text-align: center;
    font-size: 96px;
    font-weight: 700;
    padding: 48px;
    background: rgba(255,255,255,0.15);
    border-radius: 36px;
    margin-bottom: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 36px;
  }

  .tx-flag {
    font-size: 72px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  }

  .info-grid {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 60px;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .info-row .label {
    font-size: 54px;
    color: rgba(255,255,255,0.7);
  }

  .info-row .value {
    font-size: 84px;
    font-weight: 600;
  }

  .info-row .value small {
    font-size: 54px;
    opacity: 0.8;
  }

  .info-row.rate .value {
    color: #fbbf24;
  }

  .info-row .value.foreign {
    color: #60a5fa;
  }

  .info-row .value.krw {
    color: #4ade80;
  }

  /* 다중 통화 거래 리스트 (#5) */
  .multi-lines {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 48px;
    overflow-y: auto;
    flex: none;
  }

  .multi-line {
    display: grid;
    grid-template-columns: minmax(200px, auto) 1fr auto auto;
    align-items: center;
    gap: 36px;
    padding: 28px 40px;
    background: rgba(255,255,255,0.1);
    border-radius: 20px;
  }

  .multi-line-currency {
    font-size: 48px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .multi-line-flag {
    font-size: 44px;
    border-radius: 6px;
  }

  .multi-line-amount {
    font-size: 48px;
    font-weight: 600;
    color: #60a5fa;
    text-align: right;
  }

  .multi-line-amount small {
    font-size: 32px;
    opacity: 0.8;
  }

  .multi-line-rate {
    font-size: 34px;
    color: rgba(255,255,255,0.6);
  }

  .multi-line-krw {
    font-size: 48px;
    font-weight: 600;
    color: #4ade80;
    min-width: 280px;
    text-align: right;
  }

  .divider {
    height: 6px;
    background: rgba(255,255,255,0.2);
    margin: 24px 0;
  }

  .info-row.highlight {
    background: rgba(255,255,255,0.1);
    padding: 60px;
    border-radius: 36px;
    margin-top: 24px;
  }

  .info-row.highlight .label {
    font-size: 60px;
    color: rgba(255,255,255,0.9);
  }

  .info-row.highlight .value {
    font-size: 120px;
  }

  .status-bar {
    text-align: center;
    font-size: 54px;
    color: rgba(255,255,255,0.6);
    padding-top: 48px;
    border-top: 3px solid rgba(255,255,255,0.1);
  }

  .passport-request {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-size: 42px;
    color: rgba(255,255,255,0.8);
  }

  /* ========== 환율 미리보기 화면 ========== */
  .rate-preview-view {
    height: 100%;
    background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%);
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 72px;
    gap: 72px;
    position: relative;
  }

  .rate-preview-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 48px;
  }

  .rate-preview-flag {
    font-size: 120px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  .rate-preview-currency {
    font-size: 168px;
    font-weight: 700;
    color: #fbbf24;
  }

  .rate-preview-content {
    width: 100%;
    max-width: 1200px;
  }

  .rate-preview-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 72px;
  }

  .rate-preview-divider-v {
    width: 4px;
    height: 200px;
    background: rgba(255,255,255,0.2);
  }

  .rate-preview-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 36px;
    padding: 48px 72px;
    border-radius: 36px;
    transition: background 0.3s;
  }

  .rate-preview-item.active {
    background: rgba(255,255,255,0.1);
  }

  .rate-preview-label {
    font-size: 60px;
    color: #94a3b8;
  }

  .rate-preview-value {
    font-size: 120px;
    font-weight: 600;
  }

  .rate-preview-value small {
    font-size: 60px;
    opacity: 0.7;
  }

  .rate-preview-value.sell {
    color: #4ade80;
  }

  .rate-preview-value.buy {
    color: #60a5fa;
  }

  .rate-preview-single {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .rate-preview-single-value {
    font-size: 168px;
    font-weight: 700;
    color: #fbbf24;
  }

  .rate-preview-single-value small {
    font-size: 72px;
    opacity: 0.7;
  }

  .rate-preview-footer {
    font-size: 54px;
    color: rgba(255,255,255,0.5);
    padding-top: 48px;
    border-top: 3px solid rgba(255,255,255,0.1);
    width: 100%;
    text-align: center;
  }

  /* ========== 환율 티커 바 ========== */
  .rate-ticker-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: hidden;
    background: rgba(0,0,0,0.3);
    padding: 24px 0;
    white-space: nowrap;
  }

  .rate-ticker-content {
    display: inline-block;
    animation: ticker 60s linear infinite;
    font-size: 36px;
    color: rgba(255,255,255,0.8);
    font-weight: 500;
  }

  @keyframes ticker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`

export default CustomerDisplay
